/**
 * Pack Builder Component
 * Allows customers to build custom packs by selecting products by objective
 * and adding them all to cart at once.
 */

if (!customElements.get('pack-builder')) {
  class PackBuilder extends HTMLElement {
    constructor() {
      super();
      this.selectedProducts = new Map(); // variant_id -> {quantity, price, comparePrice, title, productId}
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} €';
    }

    connectedCallback() {
      this.initTabs();
      this.initProductToggles();
      this.initVariantSelectors();
      this.initAddToCartButton();
      this.initKeyboardNavigation();
    }

    /**
     * Initialize tab switching functionality
     */
    initTabs() {
      this.tabs = this.querySelectorAll('[data-tab]');
      this.panels = this.querySelectorAll('[data-panel]');

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.switchTab(tab.dataset.tab));
      });
    }

    /**
     * Switch between objective tabs
     */
    switchTab(tabId) {
      // Update tabs
      this.tabs.forEach(tab => {
        const isActive = tab.dataset.tab === tabId;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive);
      });

      // Update panels
      this.panels.forEach(panel => {
        const isActive = panel.dataset.panel === tabId;
        panel.classList.toggle('active', isActive);
        panel.hidden = !isActive;
      });
    }

    /**
     * Initialize product toggle checkboxes
     */
    initProductToggles() {
      this.toggles = this.querySelectorAll('[data-product-toggle]');

      this.toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => this.toggleProduct(e, toggle));
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleProduct(e, toggle);
          }
        });
      });
    }

    /**
     * Toggle a product selection
     */
    toggleProduct(e, toggle) {
      const productCard = toggle.closest('.pack-builder__product');
      const isAvailable = productCard.dataset.available === 'true';
      const isLocked = productCard.classList.contains('category-locked');

      if (!isAvailable || isLocked) return;

      const isSelected = toggle.classList.contains('checked');
      const variantId = productCard.dataset.variantId;
      const price = parseInt(productCard.dataset.price);
      const comparePrice = parseInt(productCard.dataset.comparePrice) || 0;
      const title = productCard.dataset.title;
      const productId = productCard.dataset.productId;
      const category = productCard.closest('.pack-builder__category');

      if (isSelected) {
        // Deselect
        toggle.classList.remove('checked');
        toggle.setAttribute('aria-checked', 'false');
        productCard.classList.remove('selected');
        this.selectedProducts.delete(variantId);

        // Unlock other products in this category
        this.unlockCategoryProducts(category);
      } else {
        // Select
        toggle.classList.add('checked');
        toggle.setAttribute('aria-checked', 'true');
        productCard.classList.add('selected', 'just-selected');
        setTimeout(() => productCard.classList.remove('just-selected'), 300);

        this.selectedProducts.set(variantId, {
          quantity: 1,
          price: price,
          comparePrice: comparePrice,
          title: title,
          productId: productId
        });

        // Lock other products in this category
        this.lockCategoryProducts(category, productCard);
      }

      this.updateSummary();
    }

    /**
     * Lock other products in the same category
     */
    lockCategoryProducts(category, selectedProduct) {
      if (!category) return;

      const products = category.querySelectorAll('.pack-builder__product');
      products.forEach(product => {
        if (product !== selectedProduct) {
          product.classList.add('category-locked');
        }
      });
    }

    /**
     * Unlock all products in the category
     */
    unlockCategoryProducts(category) {
      if (!category) return;

      const products = category.querySelectorAll('.pack-builder__product');
      products.forEach(product => {
        product.classList.remove('category-locked');
      });
    }

    /**
     * Initialize variant selectors
     */
    initVariantSelectors() {
      this.variantSelects = this.querySelectorAll('[data-variant-select]');

      this.variantSelects.forEach(select => {
        select.addEventListener('change', (e) => this.updateVariant(e, select));
      });
    }

    /**
     * Update variant selection and price
     */
    updateVariant(e, select) {
      const productCard = select.closest('.pack-builder__product');
      const productId = select.dataset.productId;
      const variantsJson = productCard.querySelector('[data-variants-json]');

      if (!variantsJson) return;

      const variants = JSON.parse(variantsJson.textContent);

      // Get all option values for this product
      const optionSelects = productCard.querySelectorAll('[data-variant-select]');
      const selectedOptions = [];
      optionSelects.forEach(sel => {
        selectedOptions.push(sel.value);
      });

      // Find matching variant
      const matchingVariant = variants.find(variant => {
        return selectedOptions.every((option, index) => {
          return variant[`option${index + 1}`] === option;
        });
      });

      if (matchingVariant) {
        const oldVariantId = productCard.dataset.variantId;
        const newVariantId = matchingVariant.id.toString();

        // Update product card data
        productCard.dataset.variantId = newVariantId;
        productCard.dataset.price = matchingVariant.price;
        productCard.dataset.comparePrice = matchingVariant.compare_at_price || 0;
        productCard.dataset.available = matchingVariant.available;

        // Update price display
        const priceElement = productCard.querySelector('[data-product-price]');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(matchingVariant.price);
        }

        // Update compare price
        const comparePriceElement = productCard.querySelector('.pack-builder__price-compare');
        if (comparePriceElement) {
          if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
            comparePriceElement.textContent = this.formatMoney(matchingVariant.compare_at_price);
            comparePriceElement.style.display = '';
          } else {
            comparePriceElement.style.display = 'none';
          }
        }

        // Update stock badge
        this.updateStockBadge(productCard, matchingVariant);

        // Update availability styling
        productCard.classList.toggle('out-of-stock', !matchingVariant.available);

        // If product was selected, update the selection with new variant
        if (this.selectedProducts.has(oldVariantId)) {
          const existingData = this.selectedProducts.get(oldVariantId);
          this.selectedProducts.delete(oldVariantId);

          if (matchingVariant.available) {
            this.selectedProducts.set(newVariantId, {
              ...existingData,
              price: matchingVariant.price,
              comparePrice: matchingVariant.compare_at_price || 0
            });
          } else {
            // Deselect if variant not available
            const toggle = productCard.querySelector('[data-product-toggle]');
            if (toggle) {
              toggle.classList.remove('checked');
              toggle.setAttribute('aria-checked', 'false');
            }
            productCard.classList.remove('selected');
          }

          this.updateSummary();
        }
      }
    }

    /**
     * Update stock badge display
     */
    updateStockBadge(productCard, variant) {
      const stockBadge = productCard.querySelector('[data-stock-badge]');
      if (!stockBadge) return;

      stockBadge.classList.remove(
        'pack-builder__stock--in-stock',
        'pack-builder__stock--low-stock',
        'pack-builder__stock--out-of-stock'
      );

      if (variant.available) {
        if (variant.inventory_quantity <= 5 && variant.inventory_quantity > 0) {
          stockBadge.classList.add('pack-builder__stock--low-stock');
          stockBadge.textContent = `Quedan ${variant.inventory_quantity} unidades`;
        } else {
          stockBadge.classList.add('pack-builder__stock--in-stock');
          stockBadge.textContent = 'En stock';
        }
      } else {
        stockBadge.classList.add('pack-builder__stock--out-of-stock');
        stockBadge.textContent = 'Agotado';
      }
    }

    /**
     * Update the summary section
     */
    updateSummary() {
      let totalPrice = 0;
      let totalComparePrice = 0;
      let productCount = 0;

      this.selectedProducts.forEach(product => {
        totalPrice += product.price * product.quantity;
        if (product.comparePrice) {
          totalComparePrice += product.comparePrice * product.quantity;
        }
        productCount += product.quantity;
      });

      // Update count
      const countElement = this.querySelector('[data-selected-count]');
      if (countElement) {
        countElement.textContent = productCount;
      }

      // Update total price
      const totalElement = this.querySelector('[data-pack-total]');
      if (totalElement) {
        totalElement.textContent = this.formatMoney(totalPrice);
      }

      // Update compare price if exists
      const compareTotalElement = this.querySelector('[data-pack-compare-total]');
      if (compareTotalElement) {
        if (totalComparePrice > totalPrice) {
          compareTotalElement.textContent = this.formatMoney(totalComparePrice);
          compareTotalElement.style.display = '';
        } else {
          compareTotalElement.style.display = 'none';
        }
      }

      // Enable/disable submit button
      const submitButton = this.querySelector('[data-add-pack]');
      if (submitButton) {
        submitButton.disabled = productCount === 0;
      }
    }

    /**
     * Initialize add to cart button
     */
    initAddToCartButton() {
      const submitButton = this.querySelector('[data-add-pack]');
      if (submitButton) {
        submitButton.addEventListener('click', () => this.addPackToCart());
      }
    }

    /**
     * Add all selected products to cart
     */
    async addPackToCart() {
      const items = Array.from(this.selectedProducts.entries()).map(([id, data]) => ({
        id: parseInt(id),
        quantity: data.quantity
      }));

      if (items.length === 0) {
        return;
      }

      const submitButton = this.querySelector('[data-add-pack]');
      this.setLoading(true);

      try {
        // Add products sequentially to avoid rate limiting
        for (const item of items) {
          const response = await fetch(KROWN.settings.routes.cart_add_url + '.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: `id=${item.id}&quantity=${item.quantity}`
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Error adding product to cart');
          }
        }

        // Refresh cart UI
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }

        // Show success message
        this.showSuccessMessage();

        // Clear selections
        this.clearSelections();

        // Open cart drawer based on theme settings
        this.openCartDrawer();

      } catch (error) {
        console.error('Error adding pack to cart:', error);
        this.showErrorMessage(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Set loading state
     */
    setLoading(isLoading) {
      const submitButton = this.querySelector('[data-add-pack]');
      if (submitButton) {
        submitButton.classList.toggle('loading', isLoading);
        submitButton.disabled = isLoading;
      }
    }

    /**
     * Show success message
     */
    showSuccessMessage() {
      const successMessage = this.querySelector('[data-success-message]');
      if (successMessage) {
        successMessage.classList.add('show');
        setTimeout(() => {
          successMessage.classList.remove('show');
        }, 3000);
      }
    }

    /**
     * Show error message
     */
    showErrorMessage(message) {
      // You could implement a custom error display here
      alert(message || 'Ha ocurrido un error al añadir los productos al carrito.');
    }

    /**
     * Clear all selections
     */
    clearSelections() {
      this.selectedProducts.clear();

      this.querySelectorAll('.pack-builder__product.selected').forEach(product => {
        product.classList.remove('selected');
        const toggle = product.querySelector('[data-product-toggle]');
        if (toggle) {
          toggle.classList.remove('checked');
          toggle.setAttribute('aria-checked', 'false');
        }
      });

      // Unlock all category-locked products
      this.querySelectorAll('.pack-builder__product.category-locked').forEach(product => {
        product.classList.remove('category-locked');
      });

      this.updateSummary();
    }

    /**
     * Open cart drawer or navigate to cart based on theme settings
     */
    openCartDrawer() {
      // Try to open the cart sidebar if available
      const cartSidebar = document.getElementById('site-cart-sidebar');
      if (cartSidebar && typeof cartSidebar.show === 'function') {
        cartSidebar.show();
        return;
      }

      // Fallback: Try other common cart drawer selectors
      const cartDrawer = document.querySelector('[data-cart-drawer]') ||
                         document.querySelector('.cart-drawer') ||
                         document.querySelector('#cart-drawer');

      if (cartDrawer) {
        cartDrawer.classList.add('active', 'open', 'is-open');
        cartDrawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cart-open', 'overflow-hidden');
      }
    }

    /**
     * Format money according to shop format
     */
    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }

      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return this.moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
    }

    /**
     * Initialize keyboard navigation for accessibility
     */
    initKeyboardNavigation() {
      // Tab navigation with arrow keys
      this.addEventListener('keydown', (e) => {
        if (e.target.matches('[data-tab]')) {
          const tabs = Array.from(this.tabs);
          const currentIndex = tabs.indexOf(e.target);

          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % tabs.length;
            tabs[nextIndex].focus();
            tabs[nextIndex].click();
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            tabs[prevIndex].focus();
            tabs[prevIndex].click();
          }
        }
      });
    }
  }

  customElements.define('pack-builder', PackBuilder);
}
